import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Typography, useTheme } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import {
    differenceInMinutes,
    Duration,
    endOfDay,
    formatISO,
    isSameDay,
    lightFormat,
    startOfDay,
} from 'date-fns'
import { add } from 'ramda'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { SetRequired } from 'type-fest'
import { ProjectDto, TrackingDto } from '@/generated/Messages/Basic_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { dateTimeOffsetDtoToDate } from '@/helper/dateTimeOffsetDto'
import { durationToDate } from '@/helper/durationToDate'
import { stringifyId } from '@/helper/id'
import { useCurrentUserDetails } from '@/queries/useCurrentUserDetails'

export const TrackingsCalendar: React.FC<{
    trackings: TrackingDto[]
    projects: ProjectDto[]
    start: Date
}> = ({ trackings, projects, start }) => {
    const [t, { language }] = useTranslation()

    const { currentUserWorkingTimeModel: { workingTimeModel } = {} } =
        useCurrentUserDetails()

    const day = new Date()

    const noTrackingAfterDate =
        workingTimeModel?.noTrackingAfter ?
            durationToDate(day, workingTimeModel.noTrackingAfter)
        :   endOfDay(day)
    const noTrackingBeforeDate =
        workingTimeModel?.noTrackingBefore ?
            durationToDate(day, workingTimeModel.noTrackingBefore)
        :   startOfDay(day)

    const projectsColorMap = Object.fromEntries(
        projects.map(p => [stringifyId(p.id), p.displayColor])
    )

    const projectsNameMap = Object.fromEntries(
        projects.map(p => [stringifyId(p.id), p.name] as const)
    )

    const theme = useTheme()

    const navigate = useNavigate({ from: '/trackings/calendar' })

    const calendarRef = React.useRef<FullCalendar>(null)
    React.useEffect(() => {
        calendarRef.current?.getApi().gotoDate(start)
    }, [start])

    return (
        <FullCalendar
            locale={[language]}
            initialDate={start}
            ref={calendarRef}
            initialView={'timeGridWeek'}
            plugins={[timeGridPlugin, interactionPlugin]}
            headerToolbar={{ left: '', right: '' }}
            firstDay={1}
            allDaySlot={false}
            businessHours={true}
            slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                omitZeroMinute: true,
                meridiem: 'short',
                hour12: false,
            }}
            eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }}
            events={trackings.map(t => {
                const projectId = t.projectId ? stringifyId(t.projectId) : ''

                const projectColor =
                    projectsColorMap[projectId] ??
                    // Needs to be a color value and not the css variable access.
                    theme.palette.grey[600]

                return {
                    id: stringifyId(t.id),
                    start:
                        t.startedAt ?
                            dateTimeOffsetDtoToDate(t.startedAt)
                        :   undefined,
                    end:
                        t.endedAt ?
                            dateTimeOffsetDtoToDate(t.endedAt)
                        :   undefined,
                    title: projectsNameMap[projectId] ?? '',
                    backgroundColor: projectColor,
                    textColor: theme.palette.getContrastText(projectColor),
                }
            })}
            slotDuration={{ minutes: 15 }}
            slotMinTime={lightFormat(noTrackingBeforeDate, 'HH:mm')}
            slotMaxTime={lightFormat(noTrackingAfterDate, 'HH:mm')}
            eventClick={({ event: { id, start } }) => {
                assertNotNullish(start)

                return navigate({
                    to: '/trackings/calendar',
                    search: prev => ({
                        ...prev,
                        showEditTrackings: true,
                        trackingId: id,
                        day: formatISO(start, { representation: 'date' }),
                    }),
                })
            }}
            dayHeaderContent={({ text, date }) => {
                const minutes = trackings
                    .filter(
                        (
                            t
                        ): t is SetRequired<
                            typeof t,
                            'startedAt' | 'endedAt'
                        > =>
                            !!t.startedAt &&
                            !!t.endedAt &&
                            isSameDay(
                                dateTimeOffsetDtoToDate(t.startedAt),
                                date
                            )
                    )
                    .map(({ startedAt, endedAt }) =>
                        differenceInMinutes(
                            dateTimeOffsetDtoToDate(endedAt),
                            dateTimeOffsetDtoToDate(startedAt)
                        )
                    )
                    .reduce(add, 0)

                return minutes === 0 ? text : (
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            <span>{text}</span>
                            <Typography
                                variant={'subtitle2'}
                                color={'textSecondary'}
                            >
                                {t('common.timeOnly.duration', {
                                    value: {
                                        hours: Math.floor(minutes / 60),
                                        minutes: minutes % 60,
                                    } as Duration,
                                })}
                            </Typography>
                        </div>
                    )
            }}
        />
    )
}
